import { useTranslation } from "react-i18next";import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, User, TruckIcon, Calendar, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ColdChainPanel } from '@/modules/ezparcel/components/coldchain/ColdChainPanel';
import { TrailsTimeline } from '@/modules/ezparcel/components/trails/TrailsTimeline';
import { API_BASE, getAuthToken } from '@/shared/services/api';
import { getStatusColor, formatDateTime, formatCurrency } from '@/shared/utils';

const TEST_SHIPMENT_ID = 'TEST-SHIP-001';

const TEST_SHIPMENT_DETAIL = {
  id: TEST_SHIPMENT_ID,
  trackingId: 'TEST999999',
  customer: 'Acme Medical Supplies',
  service: 'Priority Cold Chain',
  status: 'in_transit',
  isColdChain: true,
  parcels: 3,
  value: 1250,
  estimatedDelivery: '2026-05-20T16:00:00Z',
  vehicleId: 'VEH-009',
  pickupAddress: '100 King St W, Toronto, ON M5X 1A9',
  deliveryAddress: '200 Bay St, Toronto, ON M5J 2J1',
  weight: '42 kg',
  priority: 'high',
  driverName: 'Carlos Mendes',
  driverId: 'DRV-001',
  temperatureProfile: {
    min: 2,
    max: 8,
    unit: '°C',
    sensorId: 'TEMP-TEST-001',
    lastReading: 4.6,
    lastUpdate: '2026-05-17T14:35:00Z',
    excursions: [],
    readings: [
      { time: '10:30', temp: 4.1 },
      { time: '11:30', temp: 4.4 },
      { time: '12:30', temp: 4.8 },
      { time: '13:30', temp: 4.5 },
      { time: '14:30', temp: 4.6 },
    ],
  },
  timeline: [
    { status: 'created', location: 'Toronto Hub', timestamp: '2026-05-17T10:00:00Z' },
    { status: 'picked_up', location: 'Acme Medical Supplies', timestamp: '2026-05-17T11:15:00Z' },
    { status: 'in_transit', location: 'Downtown Toronto Route', timestamp: '2026-05-17T14:35:00Z' },
  ],
};

const TEST_SHIPMENT_TRAIL = [
  {
    id: 'trail-test-001',
    event: 'Shipment Created',
    details: 'Manifest fixture shipment created for screenshot coverage.',
    timestamp: '2026-05-17T10:00:00Z',
    actor: 'System',
    actorType: 'system',
    location: 'Toronto Hub',
    metadata: { shipmentId: TEST_SHIPMENT_ID, trackingId: 'TEST999999' },
  },
  {
    id: 'trail-test-002',
    event: 'Temperature Sensor Paired',
    details: 'Cold chain sensor TEMP-TEST-001 paired and reporting in range.',
    timestamp: '2026-05-17T10:20:00Z',
    actor: 'Warehouse Staff',
    actorType: 'warehouse_staff',
    location: 'Toronto Hub',
    metadata: { sensorId: 'TEMP-TEST-001', range: '2-8°C' },
  },
  {
    id: 'trail-test-003',
    event: 'Departed Hub',
    details: 'Shipment departed hub with assigned vehicle VEH-009.',
    timestamp: '2026-05-17T14:35:00Z',
    actor: 'Carlos Mendes',
    actorType: 'driver',
    location: 'Downtown Toronto Route',
    attachments: [{ type: 'photo', name: 'vehicle-seal-photo.jpg' }],
  },
];

function buildAddress(address) {
  if (!address) return 'N/A';
  if (typeof address === 'string') return address;
  return [address.name, address.address, address.city, address.state, address.postal_code].filter(Boolean).join(', ') || 'N/A';
}

function normalizeEvent(event, index) {
  const eventName = event.event || event.event_type || event.event_status || 'Status Updated';
  return {
    id: event.id || `event-${index}`,
    event: eventName,
    details: event.details || event.description || event.event_description || eventName,
    timestamp: event.timestamp || event.created_at || new Date().toISOString(),
    actor: event.actor || event.actor_name || 'System',
    actorType: event.actorType || event.actor_type || 'system',
    location: event.location || 'N/A',
    severity: event.severity,
    metadata: event.metadata,
    attachments: event.attachments || [],
  };
}

function normalizeShipment(raw) {
  const packages = raw.packages || [];
  const events = (raw.events || []).map(normalizeEvent);
  const declaredValue = raw.financials?.declared_value ?? raw.declared_value ?? raw.value ?? 0;
  const estimatedDelivery = raw.delivery?.window_end || raw.delivery_window_end || raw.estimatedDelivery || raw.timestamps?.created_at || raw.created_at;

  return {
    id: raw.shipment_id || raw.id,
    trackingId: raw.tracking_number || raw.trackingId || raw.shipment_id || raw.id,
    customer: raw.customer?.name || raw.customer_name || raw.customer || 'N/A',
    service: raw.service || raw.shipment_type || raw.carrier || 'Standard',
    status: raw.status || 'pending',
    isColdChain: Boolean(raw.isColdChain || raw.is_cold_chain || raw.shipment_type === 'cold_chain'),
    parcels: packages.length || raw.parcels || raw.total_packages || 1,
    value: declaredValue,
    estimatedDelivery,
    vehicleId: raw.driver?.vehicle_id || raw.vehicle_id || raw.vehicleId || 'N/A',
    pickupAddress: buildAddress(raw.origin || raw.pickupAddress || raw.origin_address),
    deliveryAddress: buildAddress(raw.destination || raw.deliveryAddress || raw.destination_address),
    weight: raw.weight || (packages[0]?.weight_kg ? `${packages[0].weight_kg} kg` : 'N/A'),
    priority: raw.priority || 'medium',
    driverName: raw.driver?.name || raw.driver_name || raw.driverName || 'Unassigned',
    driverId: raw.driver?.id || raw.driver_id || raw.driverId || 'N/A',
    temperatureProfile: raw.temperatureProfile || raw.temperature_profile || null,
    timeline: events.map((event) => ({
      status: event.event,
      location: event.location,
      timestamp: event.timestamp,
    })),
    trail: events,
  };
}

export const ShipmentDetail = () => {const { t } = useTranslation("ezparcel");
  const { id } = useParams();
  const [shipment, setShipment] = useState(null);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadShipment() {
      setLoading(true);
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE}/api/ezparcel/shipments/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Shipment detail request failed: ${response.status}`);
        }

        const data = await response.json();
        const normalized = normalizeShipment(data);
        if (active) {
          setShipment(normalized);
          setTrail(normalized.trail || []);
        }
      } catch (error) {
        if (active && id === TEST_SHIPMENT_ID) {
          setShipment(TEST_SHIPMENT_DETAIL);
          setTrail(TEST_SHIPMENT_TRAIL);
        } else if (active) {
          setShipment(null);
          setTrail([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadShipment();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{t("loadingShipment", "Loading shipment details")}</p>
      </div>);
  }

  if (!shipment) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">{t("shipmentNotFound", "Shipment Not Found")}</h2>
        <p className="text-muted-foreground mb-6">{t("theShipmentYoure", "The shipment you're looking for doesn't exist")}</p>
        <Link to="/shipments">
          <Button>{t("backToShipments", "Back to Shipments")}</Button>
        </Link>
      </div>);

  }

  const getStatusLabel = (status) => {
    return String(status || '').replace(/_/g, ' ').toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/shipments">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{shipment.trackingId}</h1>
              <Badge className={getStatusColor(shipment.status)}>
                {getStatusLabel(shipment.status)}
              </Badge>
              {shipment.isColdChain &&
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">{t("coldChain", "COLD CHAIN")}

              </Badge>
              }
            </div>
            <p className="text-muted-foreground mt-2">
              {shipment.customer} • {shipment.service}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">{t("trackLive", "Track Live")}</Button>
          <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">{t("updateStatus", "Update Status")}

          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("parcels", "Parcels")}</p>
                <p className="text-2xl font-bold">{shipment.parcels}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("value", "Value")}</p>
                <p className="text-2xl font-bold">{formatCurrency(shipment.value)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("estDelivery", "Est. Delivery")}</p>
                <p className="text-sm font-bold">{formatDateTime(shipment.estimatedDelivery)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TruckIcon className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("vehicle", "Vehicle")}</p>
                <p className="text-lg font-bold">{shipment.vehicleId}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shipment Details and Cold Chain */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("shipmentDetails", "Shipment Details")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t("pickupAddress", "Pickup Address")}</p>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-sm">{shipment.pickupAddress}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t("deliveryAddress", "Delivery Address")}</p>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-sm">{shipment.deliveryAddress}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t("weight", "Weight")}</p>
                  <p className="font-medium mt-1">{shipment.weight}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("priority", "Priority")}</p>
                  <Badge className={`mt-1 status-badge ${
                  shipment.priority === 'high' ?
                  'priority-high' :
                  shipment.priority === 'medium' ?
                  'priority-medium' :
                  'priority-low'}`
                  }>
                    {shipment.priority.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("assignedResources", "Assigned Resources")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">{t("driver", "Driver")}</p>
              <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary)/.1)] flex items-center justify-center">
                  <User className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <p className="font-medium">{shipment.driverName}</p>
                  <p className="text-sm text-muted-foreground">{shipment.driverId}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">{t("vehicle", "Vehicle")}</p>
              <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent)/.1)] flex items-center justify-center">
                  <TruckIcon className="h-5 w-5 text-[hsl(var(--accent))]" />
                </div>
                <div>
                  <p className="font-medium">{shipment.vehicleId}</p>
                  <p className="text-sm text-muted-foreground">
                    {shipment.isColdChain ? 'Refrigerated Vehicle' : 'Standard Vehicle'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cold Chain Panel (if applicable) */}
      {shipment.isColdChain && shipment.temperatureProfile &&
      <ColdChainPanel temperatureProfile={shipment.temperatureProfile} />
      }

      {/* Tabs for Trail, Documents, etc */}
      <Tabs defaultValue="trail" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trail">{t("chainOfCustody", "Chain of Custody Trail")}</TabsTrigger>
          <TabsTrigger value="timeline">{t("statusTimeline", "Status Timeline")}</TabsTrigger>
          <TabsTrigger value="documents">{t("documentsPod", "Documents & POD")}</TabsTrigger>
        </TabsList>

        <TabsContent value="trail">
          <TrailsTimeline trail={trail} />
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t("statusTimeline", "Status Timeline")}</CardTitle>
            </CardHeader>
            <CardContent>
              {shipment.timeline ?
              <div className="space-y-3">
                  {shipment.timeline.map((event, idx) =>
                <div key={idx} className="flex items-center gap-4 p-3 border border-border rounded-lg">
                      <Badge className={getStatusColor(event.status)}>
                        {getStatusLabel(event.status)}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{event.location}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                )}
                </div> :

              <p className="text-muted-foreground text-center py-8">{t("noTimelineData", "No timeline data available")}</p>
              }
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>{t("documentsProofOf", "Documents & Proof of Delivery")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">{t("documentsWillBe", "Documents will be available after delivery completion")}

              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};
